import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  ArrowUpRight,
  CheckCircle,
  WarningCircle,
  EnvelopeSimple,
  Phone,
  MapPin,
  Globe,
} from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { IMAGES } from "../data";

const INQUIRY_TYPES = [
  "B2B Purchase",
  "Export",
  "Investment",
  "Cattle (2026)",
  "Other",
];

const EXPORT_MARKETS = [
  "UAE",
  "Saudi Arabia",
  "Singapore",
  "Malaysia",
  "Sri Lanka",
  "Bahrain",
  "Qatar",
  "Oman",
  "Kuwait",
  "United Kingdom",
  "Germany",
  "Netherlands",
  "United States",
  "Canada",
];

export default function Contact() {
  const formRef = useRef(null);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [data, setData] = useState({
    user_name: "",
    user_company: "",
    user_country: "",
    user_email: "",
    user_phone: "",
    inquiry_type: INQUIRY_TYPES[0],
    message: "",
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setData((d) => ({ ...d, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });

    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      // Configuration not yet provided — log inquiry locally
      console.info("[AxonPremium] Inquiry submitted (EmailJS not configured):", data);
      setStatus({
        state: "success",
        message:
          "Inquiry received. Our trade team will be in touch shortly. (Email delivery is pending EmailJS configuration.)",
      });
      formRef.current?.reset();
      setData({
        user_name: "",
        user_company: "",
        user_country: "",
        user_email: "",
        user_phone: "",
        inquiry_type: INQUIRY_TYPES[0],
        message: "",
      });
      return;
    }

    try {
      await emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, {
        publicKey: PUBLIC_KEY,
      });
      setStatus({
        state: "success",
        message:
          "Inquiry sent. Our trade team will get back to you within one business day.",
      });
      formRef.current?.reset();
      setData({
        user_name: "",
        user_company: "",
        user_country: "",
        user_email: "",
        user_phone: "",
        inquiry_type: INQUIRY_TYPES[0],
        message: "",
      });
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus({
        state: "error",
        message:
          "Something went wrong. Please email us directly at Axonpremiumpvtltd@gmail.com.",
      });
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Get In Touch"
        title={
          <>
            Build a partnership
            <br />
            <span className="italic text-terracotta">that scales.</span>
          </>
        }
        subtitle="Trade desks, export inquiries, investment conversations, sustainability collaborations — start the conversation here."
        image={IMAGES.logistics}
        testId="contact-hero"
      />

      <section className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="contact-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Form */}
          <div className="lg:col-span-7" data-testid="contact-form-wrapper">
            <span className="overline">Inquiry Form</span>
            <h2 className="h-section text-4xl md:text-5xl mt-6 mb-12">
              Tell us what you need.
            </h2>

            <form
              ref={formRef}
              onSubmit={onSubmit}
              className="space-y-8"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <label className="overline" htmlFor="user_name">
                    Name *
                  </label>
                  <input
                    id="user_name"
                    name="user_name"
                    type="text"
                    required
                    value={data.user_name}
                    onChange={onChange}
                    placeholder="Your full name"
                    className="input-field mt-2"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="overline" htmlFor="user_company">
                    Company
                  </label>
                  <input
                    id="user_company"
                    name="user_company"
                    type="text"
                    value={data.user_company}
                    onChange={onChange}
                    placeholder="Company / Organisation"
                    className="input-field mt-2"
                    data-testid="contact-input-company"
                  />
                </div>
                <div>
                  <label className="overline" htmlFor="user_email">
                    Email *
                  </label>
                  <input
                    id="user_email"
                    name="user_email"
                    type="email"
                    required
                    value={data.user_email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    className="input-field mt-2"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className="overline" htmlFor="user_phone">
                    Phone
                  </label>
                  <input
                    id="user_phone"
                    name="user_phone"
                    type="tel"
                    value={data.user_phone}
                    onChange={onChange}
                    placeholder="+countrycode number"
                    className="input-field mt-2"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <label className="overline" htmlFor="user_country">
                    Country
                  </label>
                  <input
                    id="user_country"
                    name="user_country"
                    type="text"
                    value={data.user_country}
                    onChange={onChange}
                    placeholder="Country of operation"
                    className="input-field mt-2"
                    data-testid="contact-input-country"
                  />
                </div>
                <div>
                  <label className="overline" htmlFor="inquiry_type">
                    Nature of Inquiry *
                  </label>
                  <select
                    id="inquiry_type"
                    name="inquiry_type"
                    required
                    value={data.inquiry_type}
                    onChange={onChange}
                    className="input-field mt-2 appearance-none bg-transparent pr-8"
                    data-testid="contact-select-inquiry"
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="overline" htmlFor="message">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={data.message}
                  onChange={onChange}
                  placeholder="Volume, SKU profile, target geography, timelines…"
                  className="input-field mt-2 resize-none"
                  data-testid="contact-input-message"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-6 pt-4">
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {status.state === "loading" ? "Sending..." : "Send Inquiry"}
                  <ArrowUpRight size={14} weight="bold" />
                </button>
                <span className="text-xs font-mono uppercase tracking-widest2 text-olive-soft">
                  We respond within one business day.
                </span>
              </div>

              {status.state === "success" && (
                <div
                  className="flex gap-3 items-start border border-olive/20 bg-bone p-5"
                  data-testid="contact-success-message"
                >
                  <CheckCircle size={22} weight="duotone" className="text-olive shrink-0 mt-0.5" />
                  <p className="text-sm text-olive-deep">{status.message}</p>
                </div>
              )}
              {status.state === "error" && (
                <div
                  className="flex gap-3 items-start border border-terracotta/30 bg-terracotta/5 p-5"
                  data-testid="contact-error-message"
                >
                  <WarningCircle size={22} weight="duotone" className="text-terracotta shrink-0 mt-0.5" />
                  <p className="text-sm text-olive-deep">{status.message}</p>
                </div>
              )}
            </form>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5 space-y-12" data-testid="contact-sidebar">
            <div>
              <span className="overline">Headquarters</span>
              <div className="mt-4 flex gap-4 items-start">
                <MapPin size={22} weight="duotone" className="text-terracotta mt-1 shrink-0" />
                <p className="text-olive-deep leading-relaxed">
                  AxonPremium Agribusiness Pvt. Ltd.
                  <br />
                  6/117 B street no vishwas nagar
                  <br />
                  delhi 110032, India
                </p>
              </div>
            </div>

            <div>
              <span className="overline">Direct Lines</span>
              <ul className="mt-4 space-y-4">
                <li className="flex gap-4 items-start">
                  <EnvelopeSimple size={22} weight="duotone" className="text-terracotta mt-1 shrink-0" />
                  <div className="text-sm">
                    <div className="text-olive-soft font-mono uppercase tracking-widest2 text-[0.62rem]">
                      Trade & Export
                    </div>
                    <a
                      href="mailto:Axonpremiumpvtltd@gmail.com"
                      className="text-olive-deep hover:text-terracotta"
                      data-testid="contact-email-trade"
                    >
                      Axonpremiumpvtltd@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex gap-4 items-start">
                  <EnvelopeSimple size={22} weight="duotone" className="text-terracotta mt-1 shrink-0" />
                  <div className="text-sm">
                    <div className="text-olive-soft font-mono uppercase tracking-widest2 text-[0.62rem]">
                      Investors
                    </div>
                    <a
                      href="mailto:Axonpremiumpvtltd@gmail.com"
                      className="text-olive-deep hover:text-terracotta"
                      data-testid="contact-email-investors"
                    >
                      Axonpremiumpvtltd@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex gap-4 items-start">
                  <Phone size={22} weight="duotone" className="text-terracotta mt-1 shrink-0" />
                  <div className="text-sm">
                    <div className="text-olive-soft font-mono uppercase tracking-widest2 text-[0.62rem]">
                      Phone
                    </div>
                    <a
                      href="tel:+918800395365"
                      className="text-olive-deep hover:text-terracotta"
                      data-testid="contact-phone"
                    >
                      +91 88003 95365
                    </a>
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <span className="overline">Global Reach</span>
              <div className="mt-4 flex gap-4 items-start">
                <Globe size={22} weight="duotone" className="text-terracotta mt-1 shrink-0" />
                <p className="text-olive-deep leading-relaxed text-sm">
                  Currently shipping to <strong>14 destinations</strong> across the
                  Middle East, South-East Asia, Europe and North America.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="export-markets">
                {EXPORT_MARKETS.map((m) => (
                  <span
                    key={m}
                    className="text-[0.62rem] font-mono uppercase tracking-widest2 border border-olive/20 px-2.5 py-1 text-olive-soft"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Map / Reach Visual */}
      <section
        className="relative bg-olive-deep text-bone overflow-hidden"
        data-testid="contact-map-section"
      >
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="overline text-terracotta">Pan-India · Global Export</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-6 leading-tight">
              From a single farm in Gujarat to{" "}
              <span className="italic">14 destinations.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <img
              src={IMAGES.logistics}
              alt="Global export logistics"
              className="w-full h-[360px] object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
